import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import Icon from "components/AppIcon";
import Button from "components/ui/Button";
import Input from "components/ui/Input";

const SALUTATIONS = ["Mr.", "Mrs.", "Miss", "Dr.", "M/s"];

const REGISTRATION_SCHEMES = [
  { value: "CRN", label: "CRN" },
  { value: "MOM", label: "MOM" },
  { value: "MLS", label: "MLS" },
  { value: "SAG", label: "SAG" },
  { value: "NAT", label: "NAT" },
  { value: "GCC", label: "GCC" },
  { value: "IQA", label: "IQA" },
  { value: "OTH", label: "Other" },
];

const BALANCE_TYPES = [
  { value: "owes_you", label: "Client Owes You", icon: "TrendingUp" },
  { value: "you_owe", label: "You Owe Client", icon: "TrendingDown" },
];

const EMPTY_FORM = {
  salutation: "",
  name: "",
  additionalDetail: "",
  contact: "",
  email: "",
  vatNumber: "",
  crNumber: "",
  registrationScheme: "CRN",
  address: {
    streetAddress: "",
    buildingNumber: "",
    plotIdentification: "",
    city: "",
    citySubdivision: "",
    provinceRegion: "",
    postalCode: "",
  },
  openingBalance: "",
  balanceType: "owes_you",
};

const SectionHeader = ({ icon, title }) => (
  <div className="flex items-center gap-2 mb-3">
    <div className="w-6 h-6 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
      <Icon name={icon} size={13} className="text-primary" />
    </div>
    <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
      {title}
    </span>
    <div className="flex-1 h-px bg-border" />
  </div>
);

const Field = ({ label, required, error, children }) => (
  <div>
    <label className="block text-sm font-medium text-foreground mb-1.5">
      {label}
      {required && <span className="text-red-500 ms-0.5">*</span>}
    </label>
    {children}
    {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
  </div>
);

const AddClientModal = ({ existingClient, onSave, onClose }) => {
  const { t } = useTranslation("pages");
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const isEdit = !!existingClient;

  useEffect(() => {
    if (existingClient) {
      setForm({
        ...EMPTY_FORM,
        ...existingClient,
        address: { ...EMPTY_FORM.address, ...(existingClient.address || {}) },
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [existingClient]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const setAddr = (field, value) => {
    setForm((prev) => ({
      ...prev,
      address: { ...prev.address, [field]: value },
    }));

    const key = `address.${field}`;
    if (errors[key]) setErrors((prev) => ({ ...prev, [key]: "" }));
  };

  const validate = () => {
    const e = {};

    if (!form.name.trim()) e.name = t("clientModal.validation.nameRequired");

    if (form.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
      e.email = t("clientModal.validation.emailInvalid");
    }

    if (form.contact && !/^\+?[\d\s\-()\s]{7,20}$/.test(form.contact)) {
      e.contact = t("clientModal.validation.contactInvalid");
    }

    if (!form.vatNumber.trim()) {
      e.vatNumber = t("clientModal.validation.vatRequired");
    } else if (!/^\d{15}$/.test(form.vatNumber.trim())) {
      e.vatNumber = t("clientModal.validation.vatDigits");
    }

    if (!form.address.streetAddress.trim()) {
      e["address.streetAddress"] = t("clientModal.validation.streetRequired");
    }

    if (!form.address.buildingNumber.trim()) {
      e["address.buildingNumber"] = t(
        "clientModal.validation.buildingRequired",
      );
    }

    if (!form.address.city.trim()) {
      e["address.city"] = t("clientModal.validation.cityRequired");
    }

    if (!form.address.postalCode.trim()) {
      e["address.postalCode"] = t("clientModal.validation.postalRequired");
    }

    if (form.openingBalance && isNaN(parseFloat(form.openingBalance))) {
      e.openingBalance = t("clientModal.validation.openingBalanceInvalid");
    }

    return e;
  };

  const handleSave = () => {
    const e = validate();

    if (Object.keys(e).length > 0) {
      setErrors(e);
      return;
    }

    onSave({
      salutation: form.salutation,
      name: form.name.trim(),
      additionalDetail: form.additionalDetail.trim(),
      contact: form.contact || null,
      email: form.email || null,
      vatNumber: form.vatNumber.trim(),
      crNumber: form.crNumber || null,
      registrationScheme: form.registrationScheme,
      address: {
        streetAddress: form.address.streetAddress.trim(),
        buildingNumber: form.address.buildingNumber.trim(),
        plotIdentification: form.address.plotIdentification || null,
        city: form.address.city.trim(),
        citySubdivision: form.address.citySubdivision || null,
        provinceRegion: form.address.provinceRegion || null,
        postalCode: form.address.postalCode.trim(),
      },
      ...(!isEdit
        ? {
            openingBalance: form.openingBalance
              ? parseFloat(form.openingBalance)
              : null,
            balanceType: form.balanceType,
          }
        : {}),
      ...(isEdit ? { id: existingClient.id } : {}),
    });
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0, y: 16 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.95, opacity: 0, y: 16 }}
        transition={{ duration: 0.22 }}
        className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-border w-full max-w-2xl max-h-[92vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b border-border flex items-center justify-between bg-muted/30 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
              <Icon name="UserPlus" size={18} className="text-primary" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-foreground">
                {isEdit
                  ? t("clientModal.titleEdit")
                  : t("clientModal.titleAdd")}
              </h2>
              <p className="text-xs text-muted-foreground mt-0.5">
                {isEdit
                  ? t("clientModal.subtitleEdit")
                  : t("clientModal.subtitleAdd")}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-muted rounded-lg transition-colors"
          >
            <Icon name="X" size={18} className="text-muted-foreground" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 px-6 py-5 space-y-6">
          <div>
            <SectionHeader
              icon="Building2"
              title={t("clientModal.sections.company")}
            />
            <div className="space-y-4">
              <Field
                label={t("clientModal.fields.clientName")}
                required
                error={errors.name}
              >
                <div className="flex gap-1 flex-wrap mb-2">
                  {SALUTATIONS.map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() =>
                        set("salutation", form.salutation === s ? "" : s)
                      }
                      className={`px-3 py-1.5 rounded-lg text-xs border transition-all duration-150 whitespace-nowrap ${
                        form.salutation === s
                          ? "bg-primary text-primary-foreground border-primary"
                          : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
                <Input
                  type="text"
                  value={form.name}
                  onChange={(e) => set("name", e.target.value)}
                  placeholder={t("clientModal.placeholders.clientName")}
                  className={errors.name ? "border-red-500" : ""}
                />
              </Field>

              <Field label={t("clientModal.fields.additionalDetail")}>
                <Input
                  type="text"
                  value={form.additionalDetail}
                  onChange={(e) => set("additionalDetail", e.target.value)}
                  placeholder={t("clientModal.placeholders.additionalDetail")}
                />
              </Field>
            </div>
          </div>

          <div>
            <SectionHeader
              icon="FileText"
              title={t("clientModal.sections.tax")}
            />
            <div className="space-y-4">
              <Field
                label={t("clientModal.fields.vatNumber")}
                required
                error={errors.vatNumber}
              >
                <Input
                  type="text"
                  value={form.vatNumber}
                  onChange={(e) => set("vatNumber", e.target.value)}
                  placeholder={t("clientModal.placeholders.vatNumber")}
                  maxLength={15}
                  className={errors.vatNumber ? "border-red-500" : ""}
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("clientModal.fields.crNumber")}>
                  <Input
                    type="text"
                    value={form.crNumber}
                    onChange={(e) => set("crNumber", e.target.value)}
                    placeholder={t("clientModal.placeholders.crNumber")}
                  />
                </Field>

                <Field label={t("clientModal.fields.registrationScheme")}>
                  <div className="flex flex-wrap gap-1.5">
                    {REGISTRATION_SCHEMES.map((s) => (
                      <button
                        key={s.value}
                        type="button"
                        onClick={() => set("registrationScheme", s.value)}
                        className={`px-2.5 py-1.5 rounded-lg text-xs border transition-all duration-150 ${
                          form.registrationScheme === s.value
                            ? "bg-primary text-primary-foreground border-primary"
                            : "border-border text-muted-foreground hover:border-primary/50 hover:text-foreground"
                        }`}
                      >
                        {s.value === "OTH" ? t("clientModal.other") : s.label}
                      </button>
                    ))}
                  </div>
                </Field>
              </div>
            </div>
          </div>

          <div>
            <SectionHeader
              icon="MapPin"
              title={t("clientModal.sections.address")}
            />
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={t("clientModal.fields.streetAddress")}
                  required
                  error={errors["address.streetAddress"]}
                >
                  <Input
                    type="text"
                    value={form.address.streetAddress}
                    onChange={(e) => setAddr("streetAddress", e.target.value)}
                    placeholder={t("clientModal.placeholders.streetAddress")}
                    className={
                      errors["address.streetAddress"] ? "border-red-500" : ""
                    }
                  />
                </Field>

                <Field
                  label={t("clientModal.fields.buildingNumber")}
                  required
                  error={errors["address.buildingNumber"]}
                >
                  <Input
                    type="text"
                    value={form.address.buildingNumber}
                    onChange={(e) => setAddr("buildingNumber", e.target.value)}
                    placeholder={t("clientModal.placeholders.buildingNumber")}
                    className={
                      errors["address.buildingNumber"] ? "border-red-500" : ""
                    }
                  />
                </Field>
              </div>

              <Field label={t("clientModal.fields.plotIdentification")}>
                <Input
                  type="text"
                  value={form.address.plotIdentification}
                  onChange={(e) =>
                    setAddr("plotIdentification", e.target.value)
                  }
                  placeholder={t(
                    "clientModal.placeholders.plotIdentification",
                  )}
                />
              </Field>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={t("clientModal.fields.city")}
                  required
                  error={errors["address.city"]}
                >
                  <Input
                    type="text"
                    value={form.address.city}
                    onChange={(e) => setAddr("city", e.target.value)}
                    placeholder={t("clientModal.placeholders.city")}
                    className={errors["address.city"] ? "border-red-500" : ""}
                  />
                </Field>

                <Field label={t("clientModal.fields.citySubdivision")}>
                  <Input
                    type="text"
                    value={form.address.citySubdivision}
                    onChange={(e) => setAddr("citySubdivision", e.target.value)}
                    placeholder={t("clientModal.placeholders.citySubdivision")}
                  />
                </Field>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("clientModal.fields.provinceRegion")}>
                  <Input
                    type="text"
                    value={form.address.provinceRegion}
                    onChange={(e) => setAddr("provinceRegion", e.target.value)}
                    placeholder={t("clientModal.placeholders.provinceRegion")}
                  />
                </Field>

                <Field
                  label={t("clientModal.fields.postalCode")}
                  required
                  error={errors["address.postalCode"]}
                >
                  <Input
                    type="text"
                    value={form.address.postalCode}
                    onChange={(e) => setAddr("postalCode", e.target.value)}
                    placeholder={t("clientModal.placeholders.postalCode")}
                    className={
                      errors["address.postalCode"] ? "border-red-500" : ""
                    }
                  />
                </Field>
              </div>
            </div>
          </div>

          <div>
            <SectionHeader
              icon="Phone"
              title={t("clientModal.sections.contact")}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label={t("clientModal.fields.email")} error={errors.email}>
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => set("email", e.target.value)}
                  placeholder={t("clientModal.placeholders.email")}
                  className={errors.email ? "border-red-500" : ""}
                />
              </Field>

              <Field label={t("clientModal.fields.phone")} error={errors.contact}>
                <Input
                  type="tel"
                  value={form.contact}
                  onChange={(e) => set("contact", e.target.value)}
                  placeholder={t("clientModal.placeholders.phone")}
                  className={errors.contact ? "border-red-500" : ""}
                />
              </Field>
            </div>
          </div>

          <div>
            <SectionHeader
              icon="Wallet"
              title={t("clientModal.sections.openingBalance")}
            />
            {isEdit && (
              <p className="text-xs text-muted-foreground mb-3">
                {t("clientModal.openingBalanceLocked")}
              </p>
            )}
            <div className="space-y-3">
              <Field
                label={t("clientModal.fields.openingBalance")}
                error={errors.openingBalance}
              >
                <Input
                  type="number"
                  value={form.openingBalance}
                  onChange={(e) => set("openingBalance", e.target.value)}
                  placeholder={t("clientModal.placeholders.openingBalance")}
                  min="0"
                  step="0.01"
                  disabled={isEdit}
                  className={errors.openingBalance ? "border-red-500" : ""}
                />
              </Field>

              <AnimatePresence>
                {form.openingBalance && parseFloat(form.openingBalance) > 0 && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                    className="grid grid-cols-2 gap-3"
                  >
                    {BALANCE_TYPES.map((bt) => (
                      <button
                        key={bt.value}
                        type="button"
                        disabled={isEdit}
                        onClick={() => !isEdit && set("balanceType", bt.value)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-sm transition-all duration-150 ${
                          isEdit ? "opacity-60 cursor-not-allowed" : ""
                        } ${
                          form.balanceType === bt.value
                            ? bt.value === "owes_you"
                              ? "border-green-500 bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-300"
                              : "border-red-400 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-300"
                            : "border-border text-muted-foreground hover:border-primary/50"
                        }`}
                      >
                        <Icon name={bt.icon} size={16} />
                        {bt.value === "owes_you"
                          ? t("clientModal.balanceTypes.owesYou")
                          : t("clientModal.balanceTypes.youOwe")}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-border bg-muted/30 flex items-center justify-end gap-3 flex-shrink-0">
          <Button variant="outline" size="md" onClick={onClose}>
            {t("common:actions.close")}
          </Button>
          <Button
            variant="default"
            size="md"
            iconName={isEdit ? "Save" : "UserPlus"}
            onClick={handleSave}
          >
            {isEdit
              ? t("clientModal.actions.saveChanges")
              : t("clientModal.actions.saveAndExit")}
          </Button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default AddClientModal;
